import { ADMIN_DOOR_WORDING } from '@/lib/admin-labels';
import { type AdminRailIcon, activeRailId, railFor } from '@/lib/admin-nav';
import { useAdminSession, useAdminSignOut } from '@/lib/admin-session';
import { useIsVitrine } from '@/lib/use-instance';
import { useReplaySettings } from '@/lib/use-replay-settings';
import { AppShell, type NavItem, type RenderLink } from '@pms-digital/design-system';
import { useLinkProps, useRouterState } from '@tanstack/react-router';
import type { AnchorHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react';

/**
 * The administration window: the package `AppShell` (issue #24 drew a rail of its own; the
 * design system now owns the rail, the header, the phone menu and the profile menu).
 *
 * The catalogue stays `lib/admin-nav.ts`, untouched: the same entries in the same order, the
 * bottom pair (Médiathèque, Réglages) at the bottom of the rail by `placement: 'bottom'`, and
 * the NPC entry only where the replay is open. Two things the shell would not guarantee are
 * still decided here:
 *
 * - **Which entry is lit**: `activeRailId`, never the router. The router's notion of active is
 *   « the path is a prefix », which would light Sessions on every screen, and a second
 *   `aria-current="page"` once slipped through every test.
 * - **The accessible name of an entry is its `label`**, which is also what the e2e suite selects
 *   by (`getByRole('link', { name })`). The package draws the label under the icon, so the name
 *   is now painted as well as spoken.
 *
 * What the package does differently, and the app adopts as is: below 768 px the rail becomes the
 * package's full-screen menu (it was a bottom tab bar), and the way out is the « Se déconnecter »
 * entry of the profile menu at the bottom of the rail (it was a lone icon).
 */

/** Material Symbols for the rail entries (they were lucide icons). */
const ICONS: Record<AdminRailIcon, string> = {
  sessions: 'event_note',
  interviews: 'forum',
  experiences: 'science',
  protocol: 'assignment',
  criteria: 'checklist',
  personas: 'theater_comedy',
  npc: 'smart_toy',
  mediatheque: 'photo_library',
  settings: 'settings',
};

/** The two letters of the avatar, from the local part of the address. */
function initialsOf(email: string | null): string {
  if (email === null) return '';
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter((part) => part.length > 0);
  const letters =
    parts.length > 1 ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}` : local.slice(0, 2);
  return letters.toUpperCase();
}

type RouterAnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  ref?: Ref<HTMLAnchorElement> | undefined;
  'data-status'?: string | undefined;
};

/**
 * The router's anchor, with the shell's classes and the shell's `aria-current`.
 *
 * A component and not a call in `renderLink`: `useLinkProps` is a hook, and `renderLink` is a
 * plain function the shell calls while it renders. The router's own `aria-current`, `data-status`
 * and `className` are dropped - TanStack CONCATENATES classes, and its `aria-current` is the
 * prefix match this file exists to refuse.
 */
function RouterAnchor({
  href,
  className,
  onClick,
  children,
  ...aria
}: {
  href: string;
  className: string;
  'aria-current'?: 'page';
  'aria-label'?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
}) {
  const {
    'aria-current': _routerCurrent,
    'data-status': _routerStatus,
    className: _routerClassName,
    onClick: routerClick,
    href: routerHref,
    ...rest
  } = useLinkProps({ to: href }) as RouterAnchorProps;
  return (
    <a
      {...rest}
      href={routerHref ?? href}
      {...aria}
      className={className}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) routerClick?.(event);
      }}
    >
      {children}
    </a>
  );
}

const renderLink: RenderLink = (props) => <RouterAnchor {...props} />;

export function AdminAppShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const active = activeRailId(pathname);
  const { signOut } = useAdminSignOut();
  /*
   * `null` is a machine and not an absence to hide (T6/T8): the caller came in by the shared
   * token, which names nobody. The header says so where it names the operator.
   */
  const { email } = useAdminSession();
  /*
   * The NPC entry exists only where the replay is open (npc.md H2): the probe is the one
   * Réglages > Rejeu asks, under the same key. The vitrine has none of it.
   */
  const replay = useReplaySettings();
  const vitrine = useIsVitrine();
  const { top, bottom } = railFor({ replay: replay.data != null, vitrine });

  const nav: NavItem[] = [
    ...top.map((entry) => ({ entry, placement: 'top' as const })),
    ...bottom.map((entry) => ({ entry, placement: 'bottom' as const })),
  ].map(({ entry, placement }) => ({
    id: entry.id,
    label: entry.label,
    icon: ICONS[entry.icon],
    href: entry.to,
    active: active === entry.id,
    placement,
  }));

  return (
    <AppShell
      nav={nav}
      profile={{
        label: 'Profil',
        // No initials for a machine session: the package then draws its profile icon.
        ...(email === null ? {} : { initials: initialsOf(email) }),
        identifier: email ?? ADMIN_DOOR_WORDING.machineSession,
        // A machine session has no cookie to close; the entry then only closes the menu.
        ...(email === null ? {} : { onSignOut: signOut }),
      }}
      homeHref="/admin/sessions"
      logoLabel="Sessions, accueil de l'administration"
      renderLink={renderLink}
    >
      {children}
    </AppShell>
  );
}
